import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { ROUTES } from '@shared/config/routes';
import { Button, Checkbox, Modal } from '@shared/ui';

import type { ConsentDecision } from '../model/consentStore';
import styles from './ConsentBanner.module.css';
import { useConsent } from '../model/useConsent';

/**
 * Баннер и диалог настроек согласия (§ 25 TDDDG).
 *
 * Требования к оформлению — не вкус, а условия действительности согласия
 * (OH DSK «Digitale Dienste» v1.2, EDSA 03/2022):
 *
 * * **«Alle akzeptieren» и «Nur notwendige» — на одном уровне**, один клик
 *   каждая, одинакового визуального веса: обе синим контуром
 *   (`outline-action`). Ни одна не красная — красная заливка значит «главное
 *   действие» (DESIGN_SYSTEM §2.2), и окрашенное так согласие подталкивало бы
 *   к нему. Крупная яркая кнопка рядом с бледной ссылкой «Einstellungen» —
 *   классический dark pattern, который суды не признают;
 * * **ни один переключатель не отмечен заранее.** Заранее отмеченная галочка
 *   — не согласие (Planet49, C-673/17);
 * * **баннер не блокирует сайт**: юридические страницы обязаны быть доступны
 *   без входа и без согласия;
 * * `necessary` в списке показывается как факт («immer aktiv»), а не как
 *   переключатель: выбора там нет, и рисовать его — имитация согласия.
 *
 * Ничего не рисуется, пока оператор не объявил ни одной необязательной
 * категории: спрашивать согласие не на что.
 */
export function ConsentBanner() {
  const consent = useConsent();

  if (consent.categories.length === 0) return null;

  return (
    <>
      {consent.bannerOpen ? <Banner /> : null}
      {consent.settingsOpen ? <SettingsDialog /> : null}
    </>
  );
}

function Banner() {
  const { t } = useTranslation();
  const consent = useConsent();

  return (
    <aside
      className={styles.banner}
      role="region"
      aria-label={t('consent.title')}
      // Не `role="dialog"` и не модалка: баннер не должен запирать фокус на
      // странице, где по закону обязан быть доступен Impressum.
    >
      <div className={styles.text}>
        <h2 className={styles.title}>{t('consent.title')}</h2>
        <p className={styles.body}>
          {t('consent.body')} <Link to={ROUTES.cookies}>{t('consent.moreLink')}</Link>
        </p>
      </div>

      <div className={styles.actions}>
        {/* Вес кнопок равен: один вариант, один размер. Отказ не тяжелее
            согласия, согласие не ярче отказа (§ 25 TDDDG). */}
        <Button
          variant="outline-action"
          onClick={() => {
            consent.acceptAll();
          }}
        >
          {t('consent.acceptAll')}
        </Button>
        <Button
          variant="outline-action"
          onClick={() => {
            consent.denyAll();
          }}
        >
          {t('consent.denyAll')}
        </Button>
        <Button
          variant="ghost"
          onClick={() => {
            consent.openSettings();
          }}
        >
          {t('consent.settings')}
        </Button>
      </div>
    </aside>
  );
}

const TITLE_ID = 'consent-settings-title';
const BODY_ID = 'consent-settings-body';

function SettingsDialog() {
  const { t } = useTranslation();
  const consent = useConsent();
  // Черновик: закрытие диалога без сохранения не должно менять решение.
  const [draft, setDraft] = useState<ConsentDecision>(() => ({ ...consent.decision }));

  return (
    <Modal open onClose={consent.closeSettings} labelledBy={TITLE_ID} describedBy={BODY_ID}>
      <h2 id={TITLE_ID} className={styles.dialogTitle}>
        {t('consent.settingsTitle')}
      </h2>
      <p id={BODY_ID} className={styles.body}>
        {t('consent.settingsBody')}
      </p>

      <ul className={styles.categories}>
        <li className={styles.category}>
          <p className={styles.categoryTitle}>{t('consent.category.necessary.title')}</p>
          <p className={styles.categoryText}>{t('consent.category.necessary.text')}</p>
          {/* Факт, а не переключатель: отключить это нельзя. */}
          <p className={styles.always}>{t('consent.alwaysActive')}</p>
        </li>

        {consent.categories.map((category) => (
          <li key={category} className={styles.category}>
            <Checkbox
              checked={draft[category]}
              onChange={(event) => {
                setDraft((previous) => ({ ...previous, [category]: event.target.checked }));
              }}
            >
              <span className={styles.categoryTitle}>
                {t(`consent.category.${category}.title`)}
              </span>
              <span className={styles.categoryText}>{t(`consent.category.${category}.text`)}</span>
            </Checkbox>
          </li>
        ))}
      </ul>

      <p className={styles.footnote}>
        {t('consent.revokeHint')} <Link to={ROUTES.cookies}>{t('consent.moreLink')}</Link>
      </p>
      {consent.decidedAt ? (
        <p className={styles.footnote}>
          {t('consent.decidedAt', { date: new Date(consent.decidedAt).toLocaleString() })}
        </p>
      ) : null}

      {/* Порядок и вес кнопок равны: отказ не тяжелее согласия. Красная —
          только «сохранить выбор»: она не подталкивает ни к одному решению. */}
      <div className={styles.dialogActions}>
        <Button
          fullWidth
          onClick={() => {
            consent.decide(draft);
          }}
        >
          {t('consent.save')}
        </Button>
        <Button
          variant="secondary"
          onClick={() => {
            consent.acceptAll();
          }}
        >
          {t('consent.acceptAll')}
        </Button>
        <Button
          variant="secondary"
          onClick={() => {
            consent.denyAll();
          }}
        >
          {t('consent.denyAll')}
        </Button>
      </div>
    </Modal>
  );
}

/** Кнопка «Cookie-Einstellungen» для подвала и правовой полосы. */
export function ConsentSettingsButton({ className }: { className?: string | undefined }) {
  const { t } = useTranslation();
  const consent = useConsent();

  if (consent.categories.length === 0) return null;

  return (
    <button
      type="button"
      className={[styles.link, className].filter(Boolean).join(' ')}
      onClick={() => {
        consent.openSettings();
      }}
    >
      {t('consent.settings')}
    </button>
  );
}
